import { BannerStatus, Button, Dialog, Text, TextLink, VSpacer } from '@/components/DesignSystem';
import { Routes } from '@/constants/Routes';
import { useDashboardNavigate } from '@/pages/CustomerDashboard/helpers';
import Error from '@mui/icons-material/Error';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Warning from '@mui/icons-material/Warning';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ProgramExpirationLearnMoreDialogProps {
  accessToken: string,
  daysUntilProgramEnd: number,
  onClose: () => void,
  status: BannerStatus,
}

export const ProgramExpirationLearnMoreDialog = ({
  accessToken,
  daysUntilProgramEnd,
  onClose,
  status,
}: ProgramExpirationLearnMoreDialogProps) => {
  const { t } = useTranslation();
  const navigateToPage = useDashboardNavigate();

  const getIcon = () => {
    let Icon = null;
    switch (status) {
      case 'info':
        Icon = InfoOutlined;
        break;
      case 'warning':
        Icon = Warning;
        break;
      case 'error':
        Icon = Error;
        break;
      default:
        return null;
    }
    return <Icon color={status} fontSize="large" />;
  };

  const getTitle = () => {
    if (daysUntilProgramEnd === 0) {
      return t($ => $.customerDashboard.programExpiration.dialog.endsToday);
    }
    return t(
      $ => $.customerDashboard.programExpiration.dialog.endsInDays,
      { count: daysUntilProgramEnd },
    );
  };

  return (
    <Dialog
      acceptButton={(props) => (
        <Button
          {...props}
          color="inherit"
          onClick={onClose}
          variant="outlined"
        >
          {t($ => $.common.actions.close)}
        </Button>
      )}
      heroIcon={getIcon()}
      onClose={onClose}
      open
      showCloseButton={false}
      testID="program-expiration-learn-more-dialog"
      title={getTitle()}
    >
      <Stack textAlign="center">
        <Text category="title-small">
          {t($ => $.customerDashboard.programExpiration.dialog.body)}
        </Text>
        <VSpacer size="3" />
        <Text category="title-small">
          {t($ => $.customerDashboard.programExpiration.dialog.checkWallet)}{' '}
          <TextLink
            onClick={() => navigateToPage(Routes.CUSTOMER_DASHBOARD_WALLET, accessToken)}
            testID="wallet-text-link"
          >
            {t($ => $.customerDashboard.programExpiration.dialog.walletLink)}
          </TextLink>
          {' '}{t($ => $.customerDashboard.programExpiration.dialog.forPointsExpiringSoon)}
        </Text>
        <VSpacer size="7" />
        <Text category="body-small">
          {t($ => $.customerDashboard.programExpiration.dialog.questions)}{' '}
          <TextLink
            fontWeight={500}
            onClick={() => navigateToPage(Routes.CUSTOMER_DASHBOARD_HOW_IT_WORKS, accessToken)}
            testID="how-it-works-text-link"
          >
            {t($ => $.customerDashboard.programExpiration.dialog.howItWorksLink)}
          </TextLink>
          {' '}{t($ => $.customerDashboard.programExpiration.dialog.pageForDetails)}
        </Text>
      </Stack>
    </Dialog>
  );
};
